import React, { useMemo, useRef, useState } from "react";
import API from "../services/api";
import BrandLogo from "../components/BrandLogo";

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const codeRefs = useRef([]);

  const authCode = useMemo(() => code.join(""), [code]);
  const canLogin = email.trim() && password.trim() && authCode.length === 6;

  const handleCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });

    if (digit && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, event) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const next = ["", "", "", "", "", ""];
    pasted.split("").forEach((digit, index) => {
      next[index] = digit;
    });
    setCode(next);
    codeRefs.current[Math.min(pasted.length, 6) - 1]?.focus();
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!canLogin) {
      setMessage("Enter email, password, and 6 digit code.");
      return;
    }

    try {
      setLoading(true);
      const res = await API.post("admin-login", {
        email: email.trim(),
        password,
        auth_code: authCode,
      });

      if (res.data && res.data.id && (res.data.role === "admin" || res.data.role === "sub_admin")) {
        localStorage.setItem("admin", JSON.stringify({ ...res.data, loginAt: Date.now() }));
        window.location.href = "/admin-dashboard";
        return;
      }

      setMessage("Invalid admin login.");
    } catch (err) {
      const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
      setMessage(serverMessage || "Login failed. Check details and authenticator code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={styles.page}>
      <div style={styles.goldGlow} />
      <img src="/logo5.png" alt="" style={styles.bgLogo} />

      <section style={styles.card}>
        <div style={styles.logoBox}>
          <BrandLogo size="sm" />
        </div>

        <div style={styles.heading}>
          <span style={styles.badge}>Secure Admin Login</span>
          <h1 style={styles.title}>Welcome Back</h1>
          <p style={styles.subtitle}>Enter your admin details and Google Authenticator code.</p>
        </div>

        <form onSubmit={handleLogin} style={styles.form}>
          <label style={styles.label}>
            Email
            <input
              type="email"
              autoComplete="username"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            Password
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
            />
          </label>

          <div>
            <div style={styles.codeLabelRow}>
              <span style={styles.labelText}>Authenticator Code</span>
              <span style={styles.codeHint}>6 digits</span>
            </div>
            <div style={styles.codeRow} onPaste={handleCodePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(node) => {
                    codeRefs.current[index] = node;
                  }}
                  value={digit}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  onChange={(event) => handleCodeChange(index, event.target.value)}
                  onKeyDown={(event) => handleCodeKeyDown(index, event)}
                  style={styles.codeBox}
                  aria-label={`Authenticator digit ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <button type="submit" disabled={!canLogin || loading} style={{ ...styles.button, ...(!canLogin || loading ? styles.buttonDisabled : null) }}>
            {loading ? "Checking..." : "Login"}
          </button>
        </form>

        {message && <p style={styles.message}>{message}</p>}
      </section>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    boxSizing: "border-box",
    background:
      "linear-gradient(145deg, #fffaf0 0%, #fff7dc 34%, #f8fafc 72%, #fffdf7 100%)",
    position: "relative",
    overflow: "hidden",
  },
  goldGlow: {
    position: "absolute",
    width: 620,
    height: 620,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(245,158,11,0.24) 0%, rgba(251,191,36,0.10) 42%, transparent 70%)",
    top: -210,
    right: -170,
    pointerEvents: "none",
  },
  bgLogo: {
    position: "absolute",
    width: "min(560px, 90vw)",
    opacity: 0.045,
    filter: "saturate(1.15)",
    pointerEvents: "none",
  },
  card: {
    width: "min(430px, 100%)",
    background: "rgba(255,255,255,0.9)",
    border: "1px solid rgba(245,158,11,0.18)",
    borderRadius: 26,
    padding: 26,
    boxShadow: "0 22px 60px rgba(120, 53, 15, 0.16)",
    position: "relative",
    zIndex: 1,
  },
  logoBox: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 12,
  },
  heading: {
    textAlign: "center",
    marginBottom: 22,
  },
  badge: {
    display: "inline-flex",
    padding: "7px 12px",
    borderRadius: 999,
    background: "#fff7ed",
    color: "#b45309",
    border: "1px solid #fed7aa",
    fontSize: 12,
    fontWeight: 900,
  },
  title: {
    margin: "12px 0 5px",
    fontSize: 30,
    lineHeight: 1.08,
    color: "#1f2937",
    letterSpacing: "-0.035em",
  },
  subtitle: {
    margin: 0,
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.45,
  },
  form: {
    display: "grid",
    gap: 14,
  },
  label: {
    display: "grid",
    gap: 7,
    color: "#334155",
    fontSize: 13,
    fontWeight: 900,
  },
  labelText: {
    color: "#334155",
    fontSize: 13,
    fontWeight: 900,
  },
  input: {
    width: "100%",
    height: 52,
    borderRadius: 15,
    border: "1px solid #e2d4bd",
    background: "#fffdf8",
    padding: "0 15px",
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
    outline: "none",
    boxSizing: "border-box",
  },
  codeLabelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  codeHint: {
    fontSize: 12,
    fontWeight: 800,
    color: "#b45309",
  },
  codeRow: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 8,
  },
  codeBox: {
    width: "100%",
    height: 54,
    borderRadius: 14,
    border: "1px solid #e0c89f",
    background: "#fffaf0",
    textAlign: "center",
    fontSize: 23,
    fontWeight: 950,
    color: "#111827",
    outline: "none",
    boxSizing: "border-box",
  },
  button: {
    width: "100%",
    height: 54,
    border: 0,
    borderRadius: 16,
    background: "linear-gradient(135deg, #d97706, #f59e0b)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(217,119,6,0.26)",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  message: {
    margin: "14px 0 0",
    borderRadius: 14,
    background: "#fff1f2",
    color: "#be123c",
    padding: "11px 12px",
    fontSize: 13,
    fontWeight: 800,
    textAlign: "center",
  },
};

export default AdminLogin;
